'use client';
import React, {
  useContext,
  createContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';

import { TourVariationViewModel } from '@api/models/TourVariationViewModel';

import { Action, TourDetailState, ACTIONS, SelectedVariations } from './types';

const initialValues = {
  selectedPromotions: {},
};

type TourDetailDispatch = (action: Action) => void;

const TourDetailStateContext = createContext<TourDetailState | undefined>(
  undefined
);
const TourDetailDispatchContext = createContext<TourDetailDispatch | undefined>(
  undefined
);

const tourDetailReducer = (
  state: TourDetailState,
  action: Action
): TourDetailState => {
  switch (action.type) {
    case ACTIONS.SET_BASKET_ID:
      return { ...state, basketId: action.payload };
    case ACTIONS.SET_PACKAGE_PROMOTION: {
      const { packageId, promotion, roomNumber } = action.payload;
      const {
        selectedPromotions: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          [packageId]: previousSelectedPromotionForCurrentPackage = [],
          ...otherPromotions
        },
      } = state;
      previousSelectedPromotionForCurrentPackage[roomNumber] = promotion;

      const selectedPromotions = promotion
        ? {
            ...otherPromotions,
            [packageId]: previousSelectedPromotionForCurrentPackage,
          }
        : otherPromotions;
      return {
        ...state,
        selectedPromotions,
      };
    }
    case ACTIONS.REMOVE_ROOM: {
      const { packageId, roomNumber } = action.payload;
      const {
        selectedPromotions: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          [packageId]: previousSelectedPromotionForCurrentPackage = [],
          ...otherPromotions
        },
      } = state;

      const nextSelectionState =
        previousSelectedPromotionForCurrentPackage.filter(
          (...[, idx]) => idx !== roomNumber
        );

      const selectedPromotions = {
        ...otherPromotions,
        [packageId]: nextSelectionState,
      };

      return {
        ...state,
        selectedPromotions,
      };
    }

    case ACTIONS.REMOVE_PACKAGE_PROMOTION: {
      const { packageId, variationId } = action.payload;
      const { selectedPromotions } = state;

      const target = selectedPromotions[packageId];
      const nextSelectionState = target?.filter(
        (item) => item.variationId !== variationId
      );

      const updatedPromotions = {
        ...selectedPromotions,
        [packageId]: nextSelectionState,
      };

      return {
        ...state,
        selectedPromotions: updatedPromotions,
      };
    }

    default:
      return state;
  }
};

const TourDetailProvider = ({
  children,
  resource = 'Tur',
}: React.PropsWithChildren<{ resource?: 'Tur' | 'Gemi' }>) => {
  const [state, dispatch] = useReducer(tourDetailReducer, {
    ...initialValues,
    resource,
  });
  const basketId =
    typeof window !== 'undefined' && localStorage.getItem('basketId');

  useEffect(() => {
    if (basketId) {
      dispatch({ type: ACTIONS.SET_BASKET_ID, payload: basketId });
    }
  }, [basketId]);

  return (
    <TourDetailStateContext.Provider value={state}>
      <TourDetailDispatchContext.Provider value={dispatch}>
        {children}
      </TourDetailDispatchContext.Provider>
    </TourDetailStateContext.Provider>
  );
};

const getSelectedVariations = (
  selectedPromotions: TourDetailState['selectedPromotions']
) => {
  if (Object.keys(selectedPromotions).length === 0) return [];
  return Object.keys(selectedPromotions).reduce((variations, key) => {
    const index: number = +key;
    const selectedPromotionsForPackage = selectedPromotions[index] ?? [];
    if (!selectedPromotionsForPackage.length) return variations;
    const selectedRoomsMetaInfo = selectedPromotionsForPackage.reduce(
      (acc, room) => {
        if (room.meta) {
          acc.push(room.meta);
        }
        return acc;
      },
      [] as unknown[]
    );
    return [...variations, ...selectedRoomsMetaInfo];
  }, [] as unknown[]) as SelectedVariations[];
};

export const useTourDetailState = () => {
  const context = useContext(TourDetailStateContext);
  if (context === undefined) {
    throw new Error(
      'useTourDetailState must be used within a TourDetailProvider'
    );
  }
  return {
    ...context,
    selectedVariations: getSelectedVariations(context.selectedPromotions),
  };
};

export const useSetBasketId = (): ((payload: string) => void) => {
  const dispatch = useContext(TourDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetBasketId must be used within a TourDetailProvider');
  }
  return (payload: string) =>
    dispatch({ type: ACTIONS.SET_BASKET_ID, payload: payload });
};

export const useSetPromotionForPackage = () => {
  const dispatch = useContext(TourDetailDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useSetSelectedVariations must be used within a TourDetailProvider'
    );
  }
  const setPromotionForPackage = useCallback(
    <Meta,>(
      packageId: number,
      promotion: TourVariationViewModel & { meta?: Meta },
      roomNumber: number
    ) => {
      dispatch({
        type: ACTIONS.SET_PACKAGE_PROMOTION,
        payload: { packageId, promotion, roomNumber },
      });
    },
    [dispatch]
  );
  const removePackage = useCallback(
    (packageId: number, roomNumber: number) => {
      dispatch({
        type: ACTIONS.SET_PACKAGE_PROMOTION,
        payload: {
          packageId,
          promotion: undefined as unknown as TourVariationViewModel,
          roomNumber,
        },
      });
    },
    [dispatch]
  );
  const removeRoom = useCallback(
    (packageId: number, roomNumber: number) => {
      dispatch({
        type: ACTIONS.REMOVE_ROOM,
        payload: {
          packageId,
          roomNumber,
        },
      });
    },
    [dispatch]
  );

  const removePackagePromotion = useCallback(
    (packageId: number, variationId: string) => {
      dispatch({
        type: ACTIONS.REMOVE_PACKAGE_PROMOTION,
        payload: {
          packageId,
          variationId,
        },
      });
    },
    [dispatch]
  );
  return {
    setPromotionForPackage,
    removePackage,
    removeRoom,
    removePackagePromotion,
  };
};

export default TourDetailProvider;
